import { useId, useMemo } from "react";
import {
  MAIN_C_STALE_MS,
  MAIN_C_SYNC_THRESHOLD_HZ,
  type SparklineFrame,
} from "../hooks/useFreqSparkline";
import type { FreqPoint } from "../types/dashboard";

/** Primary meters — solid fills (UniFi sky blue + violet). */
export const METER_SPARKLINE_SERIES = {
  main_A: { label: "A:", stroke: "#38bdf8", fillTop: 0.35, fillBottom: 0 },
  main_B: { label: "B:", stroke: "#a78bfa", fillTop: 0.32, fillBottom: 0 },
} as const;

/** Backup main_C — dashed line only, consensus-colored. */
export const METER_C_STYLES = {
  agree: { stroke: "#94a3b8", opacity: 0.75 },
  off: { stroke: "#f59e0b", opacity: 0.9 },
  stale: { stroke: "#64748b", opacity: 0.45 },
} as const;

const SERIES = METER_SPARKLINE_SERIES;
const LINE_WIDTH = 1.25;
const C_LINE_WIDTH = 1;
const C_DASH = "4 3";
/** Visual-only offset when “shift view” is on (5 mHz below true value). */
export const C_SHIFT_VIEW_OFFSET_HZ = 0.005;

const WINDOW_MS = 10_000;
const WIDTH = 260;
const HEIGHT = 40;

function visiblePoints(points: FreqPoint[], windowStart: number) {
  return points.filter((p) => p.ts_ms >= windowStart).sort((a, b) => a.ts_ms - b.ts_ms);
}

function buildStepPaths(
  points: FreqPoint[],
  windowStart: number,
  minY: number,
  yRange: number,
) {
  const visible = visiblePoints(points, windowStart);
  if (!visible.length) return null;

  const toX = (ts: number) => ((ts - windowStart) / WINDOW_MS) * WIDTH;
  const toY = (hz: number) => HEIGHT - ((hz - minY) / yRange) * HEIGHT;

  const first = visible[0]!;
  let line = `M${toX(first.ts_ms).toFixed(2)},${toY(first.freq_hz).toFixed(2)}`;
  for (let i = 1; i < visible.length; i++) {
    const p = visible[i]!;
    line += `H${toX(p.ts_ms).toFixed(2)}V${toY(p.freq_hz).toFixed(2)}`;
  }

  const area = [
    line,
    `V${HEIGHT}`,
    `H${toX(first.ts_ms).toFixed(2)}`,
    "Z",
  ].join(" ");

  return { line, area };
}

type CSegmentState = keyof typeof METER_C_STYLES;

function cStateAtFrame(f: SparklineFrame): CSegmentState | null {
  if (f.main_C == null) return null;
  const med = (f.main_A + f.main_B) / 2;
  return Math.abs(f.main_C - med) <= MAIN_C_SYNC_THRESHOLD_HZ ? "agree" : "off";
}

function buildCSegments(
  frames: SparklineFrame[],
  windowStart: number,
  minY: number,
  yRange: number,
  stale: boolean,
  cOffsetHz: number,
) {
  const visible = frames.filter((f) => f.ts_ms >= windowStart && f.main_C != null);
  if (!visible.length) return [];

  const toX = (ts: number) => ((ts - windowStart) / WINDOW_MS) * WIDTH;
  const toY = (hz: number) => HEIGHT - ((hz - minY) / yRange) * HEIGHT;
  const stateKey: CSegmentState = stale ? "stale" : "agree";

  const segments: { line: string; state: CSegmentState }[] = [];
  let currentState: CSegmentState | null = null;
  let line = "";

  for (const f of visible) {
    const state = stale ? stateKey : cStateAtFrame(f)!;
    const x = toX(f.ts_ms).toFixed(2);
    const y = toY(f.main_C! - cOffsetHz).toFixed(2);

    if (state !== currentState) {
      if (line) segments.push({ line, state: currentState! });
      line = `M${x},${y}`;
      currentState = state;
    } else {
      line += `H${x}V${y}`;
    }
  }
  if (line && currentState) segments.push({ line, state: currentState });

  return segments;
}

function buildChart(
  frames: SparklineFrame[],
  lastTsC: number | null,
  now: number,
  shiftView: boolean,
) {
  const cOffsetHz = shiftView ? C_SHIFT_VIEW_OFFSET_HZ : 0;
  const withAb = frames.filter((f) => f.ts_ms >= 0);
  if (!withAb.length) return null;

  const windowEnd = withAb.reduce((max, f) => Math.max(max, f.ts_ms), 0);
  const windowStart = windowEnd - WINDOW_MS;
  const inWindow = withAb.filter((f) => f.ts_ms >= windowStart);
  if (!inWindow.length) return null;

  const freqs: number[] = [];
  for (const f of inWindow) {
    freqs.push(f.main_A, f.main_B);
    if (f.main_C != null) freqs.push(f.main_C - cOffsetHz);
  }
  if (!freqs.length) return null;

  let minY = Math.min(...freqs);
  let maxY = Math.max(...freqs);
  const span = maxY - minY;
  const pad = span > 0 ? span * 0.15 : 0.0005;
  minY -= pad;
  maxY += pad;
  const yRange = maxY - minY || 0.001;

  const series = {
    main_A: inWindow.map((f) => ({ ts_ms: f.ts_ms, freq_hz: f.main_A, raw_u16: 0 })),
    main_B: inWindow.map((f) => ({ ts_ms: f.ts_ms, freq_hz: f.main_B, raw_u16: 0 })),
  };

  const stale = lastTsC == null || now - lastTsC > MAIN_C_STALE_MS;

  return {
    main_B: buildStepPaths(series.main_B, windowStart, minY, yRange),
    main_A: buildStepPaths(series.main_A, windowStart, minY, yRange),
    main_C: buildCSegments(inWindow, windowStart, minY, yRange, stale, cOffsetHz),
  };
}

export function FreqSparkline({
  frames,
  lastTsC,
  shiftView = false,
}: {
  frames: SparklineFrame[];
  lastTsC: number | null;
  shiftView?: boolean;
}) {
  const gradA = useId();
  const gradB = useId();
  const chart = useMemo(
    () => buildChart(frames, lastTsC, Date.now(), shiftView),
    [frames, lastTsC, shiftView],
  );

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="block h-10 w-full"
      preserveAspectRatio="none"
      role="img"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradB} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={SERIES.main_B.stroke} stopOpacity={SERIES.main_B.fillTop} />
          <stop offset="100%" stopColor={SERIES.main_B.stroke} stopOpacity={SERIES.main_B.fillBottom} />
        </linearGradient>
        <linearGradient id={gradA} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={SERIES.main_A.stroke} stopOpacity={SERIES.main_A.fillTop} />
          <stop offset="100%" stopColor={SERIES.main_A.stroke} stopOpacity={SERIES.main_A.fillBottom} />
        </linearGradient>
      </defs>
      {chart?.main_C.map((seg, i) => {
        const style = METER_C_STYLES[seg.state];
        return (
          <path
            key={`c-${i}`}
            d={seg.line}
            fill="none"
            stroke={style.stroke}
            strokeOpacity={style.opacity}
            strokeWidth={C_LINE_WIDTH}
            strokeDasharray={C_DASH}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
      {chart?.main_B && (
        <>
          <path d={chart.main_B.area} fill={`url(#${gradB})`} stroke="none" />
          <path
            d={chart.main_B.line}
            fill="none"
            stroke={SERIES.main_B.stroke}
            strokeWidth={LINE_WIDTH}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
      {chart?.main_A && (
        <>
          <path d={chart.main_A.area} fill={`url(#${gradA})`} stroke="none" />
          <path
            d={chart.main_A.line}
            fill="none"
            stroke={SERIES.main_A.stroke}
            strokeWidth={LINE_WIDTH}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
    </svg>
  );
}
